// Pure data transforms for the AI Reasoning Path module (no JSX).
// Ports the logic that used to live inline in cognition-run-output.tsx and maps
// the backend CognitionRunData contract into the view models in ./types.
// Mirrors deep-signal/builders.ts.

import { normalizeProviderKey } from '@/app/constants/models';
import type {
  CognitionRunData,
  Gap,
  DomainInfluence,
  BaselineSummary,
  AttributeComparisonRow,
  TaskSetup,
  CognitiveStage,
  CognitionHistoryItem,
  AutoExtractedProduct,
} from '@/app/services/cognition/cognition-service';

import type {
  StageId,
  Priority,
  CandidateVM,
  ModelPillVM,
  RunSummaryVM,
  OptimizationVM,
  OptimizationTarget,
  OptimizationType,
  StageVM,
  StageVarianceVM,
  GapRowVM,
  GapsSummaryVM,
  GapSeverity,
  HistoryRowVM,
} from './types';
import {
  STAGE_ACCENTS,
  STAGE_NUMBER,
  STAGE_ORDER,
  MODEL_DOT_COLORS,
  MODEL_DOT_FALLBACK,
} from './constants';
import { STAGE_ASKS, STAGE_WHY_FALLBACK, PREVIEW_STAGES } from './fixtures';

// ── small numeric/string helpers (ported) ───────────────────────────────────
export function scoreTo10(overallScore: unknown): number | null {
  const n = Number(overallScore);
  if (!Number.isFinite(n)) return null;
  return n > 10 ? n / 10 : n;
}

function fmt1(n: number | null): string {
  if (n == null) return '—';
  return n.toFixed(1);
}

function normKey(s: string): string {
  return String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function sanitizeSummaryText(raw: string): string {
  return String(raw || '')
    .replace(/<\/?strong>/gi, '')
    .replace(/strong>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/\*\*/g, '')
    .replace(/\r\n/g, '\n')
    .replace(/(\d)\s*\.\s*(\d)/g, '$1.$2')
    .replace(/([a-z0-9])\s*\.\s*(com|net|org|ai|io|co|uk|in|edu|gov)\b/gi, '$1.$2')
    .trim();
}

// ── model dot color ──────────────────────────────────────────────────────────
export function modelDotFor(idOrLabel: string): string {
  const key = normalizeProviderKey(idOrLabel);
  return MODEL_DOT_COLORS[key] || MODEL_DOT_FALLBACK;
}

export function buildModelPills(
  modelIds: string[],
  modelLabel: (id: string) => string
): ModelPillVM[] {
  return (modelIds || []).map((id) => ({
    id,
    label: modelLabel(id) || id,
    dot: modelDotFor(id),
  }));
}

// ── candidates (setup panel) ─────────────────────────────────────────────────
export function buildCandidates(
  products: AutoExtractedProduct[] | undefined,
  selectedName: string | null
): CandidateVM[] {
  const sel = String(selectedName || '').trim().toLowerCase();
  return (products || []).map((p) => ({
    id: p.id,
    name: p.name,
    match: Math.max(0, Math.min(100, Number(p.matchScore ?? 0))),
    selected: !!sel && p.name.trim().toLowerCase() === sel,
  }));
}

// ── stage priority (ported verbatim in logic) ────────────────────────────────
export function computeStagePriority(args: {
  stageId: string;
  taskSetupData: TaskSetup[];
  gapsData: Gap[];
  attributeData: AttributeComparisonRow[];
  baseline?: BaselineSummary | null;
}): { priority: Priority; reason: string } {
  const { stageId, taskSetupData, gapsData, attributeData, baseline } = args;

  const highGaps = (gapsData || []).filter((g) => String(g.severity).toLowerCase() === 'high');
  const moderateGaps = (gapsData || []).filter((g) =>
    ['moderate', 'medium'].includes(String(g.severity).toLowerCase())
  );

  if (stageId === 'task-setup') {
    const types = new Set(
      (taskSetupData || []).map((t) => String(t.taskType || '').trim()).filter(Boolean)
    );
    const personas = new Set(
      (taskSetupData || []).map((t) => String(t.persona || '').trim()).filter(Boolean)
    );
    if (types.size >= 3) return { priority: 'High', reason: 'Task framing inconsistency detected' };
    if (types.size === 2) return { priority: 'Medium', reason: 'Task framing inconsistency detected' };
    if (personas.size >= 3) return { priority: 'Medium', reason: 'Multiple personas detected' };
    return { priority: 'Low', reason: 'Task framing consistent' };
  }

  if (stageId === 'evidence-signals') {
    if (highGaps.length >= 2) return { priority: 'High', reason: `${highGaps.length} critical gaps detected` };
    if (highGaps.length === 1) return { priority: 'Medium', reason: '1 critical gap detected' };
    const divergent = (attributeData || []).filter((a) =>
      ['divergent', 'weak'].includes(String(a.consensus || '').toLowerCase())
    ).length;
    if (divergent >= 2) return { priority: 'Medium', reason: 'Data consistency issues detected' };
    return { priority: 'Low', reason: 'Evidence signals stable' };
  }

  if (stageId === 'understanding-decisions') {
    const score = baseline?.overallScore ?? null;
    if (score != null && score >= 80 && highGaps.length === 0)
      return { priority: 'Low', reason: 'Leading position maintained' };
    if (highGaps.length >= 1) return { priority: 'Medium', reason: 'Competitive risk detected' };
    if (moderateGaps.length >= 2) return { priority: 'Medium', reason: 'Competitive gaps detected' };
    return { priority: 'Low', reason: 'Competitive position stable' };
  }

  if (stageId === 'final-synthesis') {
    const divergent = (attributeData || []).filter((a) =>
      ['divergent', 'weak'].includes(String(a.consensus || '').toLowerCase())
    ).length;
    if (divergent >= 3) return { priority: 'High', reason: 'Narrative inconsistency found' };
    if (divergent >= 1) return { priority: 'Medium', reason: 'Narrative inconsistency found' };
    if (highGaps.length >= 2) return { priority: 'High', reason: `${highGaps.length} critical gaps detected` };
    if (highGaps.length === 1) return { priority: 'Medium', reason: '1 critical gap detected' };
    return { priority: 'Low', reason: 'Narrative consistent' };
  }

  return { priority: 'Medium', reason: 'Review recommended' };
}

// ── run summary (deterministic fallback, ported) ─────────────────────────────
function buildDeterministicRunSummary(args: {
  baseline?: BaselineSummary | null;
  gaps?: Gap[];
  domains?: DomainInfluence[];
  totalModels: number;
}): string {
  const baseline = args.baseline || null;
  const gaps = Array.isArray(args.gaps) ? args.gaps : [];
  const domains = Array.isArray(args.domains) ? args.domains : [];
  const totalModels = Math.max(1, Number(args.totalModels || 1));

  const s10 = baseline?.overallScore != null ? scoreTo10(baseline.overallScore) : null;
  const topDomain = domains?.[0]?.domain || baseline?.topDomain?.name || '—';
  const topInfluence = domains?.[0]?.influence ?? baseline?.topDomain?.influence ?? null;
  const topModels = domains?.[0]?.models ?? null;
  const high = gaps.filter((g) => String(g.severity).toLowerCase() === 'high');
  const pickGap = high[0] || gaps[0] || null;

  const lines: string[] = [];
  if (s10 != null) lines.push(`Overall positioning: ${fmt1(s10)}/10 across selected models.`);

  if (baseline?.topStrength || pickGap?.attribute) {
    const strength = baseline?.topStrength ? `Your strongest signal is ${baseline.topStrength}.` : '';
    const gapPart = pickGap?.attribute
      ? ` Key gap: ${pickGap.attribute}, ${pickGap.gapReason || 'missing or inconsistent information'} (affects ${pickGap.modelsAffected ?? '—'}/${totalModels} models).`
      : '';
    const combined = `${strength}${gapPart}`.trim();
    if (combined) lines.push(combined);
  }

  const weakness = baseline?.keyWeakness ? `Primary weakness signal: ${baseline.keyWeakness}.` : '';
  if (topDomain && topInfluence != null) {
    const domLine = `Top influencing domain: ${topDomain} (${fmt1(Number(topInfluence))}/10${
      topModels != null ? ` across ${topModels}/${totalModels} models` : ''
    }). ${weakness}`.trim();
    lines.push(domLine);
  } else if (weakness) {
    lines.push(weakness);
  }
  return lines.join('\n\n');
}

export function buildRunSummary(
  runResult: CognitionRunData | null,
  totalModels: number
): RunSummaryVM {
  const baseline = runResult?.baselineSummaryData || null;
  const score = baseline?.overallScore != null ? scoreTo10(baseline.overallScore) : null;

  const backend =
    runResult?.runSummary || baseline?.runSummary || baseline?.summary || '';
  const text = sanitizeSummaryText(
    String(backend || '').trim()
      ? String(backend)
      : buildDeterministicRunSummary({
          baseline,
          gaps: runResult?.gapsData,
          domains: runResult?.domainData,
          totalModels,
        })
  );

  const paragraphs = text.split(/\n\s*\n/g).map((p) => p.trim()).filter(Boolean);
  return {
    score: score == null ? null : score.toFixed(1),
    lede: paragraphs[0] || '',
    detail: paragraphs.slice(1).join(' '),
  };
}

// ── priority optimizations (ported buildActionCards + view mapping) ──────────

// Stable id for a gap's row so "See how we found this" can scroll to the exact
// gap the optimization was derived from. Shared with gaps-section.
export function gapRowId(attribute: string): string {
  return (
    'gap-row-' +
    String(attribute || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  );
}

const OPT_BADGE_LABEL: Record<OptimizationType, string> = {
  High: 'High Priority',
  Strategic: 'Strategic',
  Medium: 'Medium Priority',
};

export function buildOptimizations(
  runResult: CognitionRunData | null,
  totalModels: number
): OptimizationVM[] {
  const gaps = runResult?.gapsData || [];
  const domains = runResult?.domainData || [];
  const baseline = runResult?.baselineSummaryData || null;
  const tm = Math.max(1, Number(totalModels || 1));

  const out: Array<{
    id: string;
    type: OptimizationType;
    headline: string;
    desc: string;
    target: OptimizationTarget;
  }> = [];
  const high = gaps.filter((g) => String(g.severity).toLowerCase() === 'high');
  const secondHigh = high.length > 1 ? high[1] : null;
  const topDomain = domains?.[0];
  const weakness = baseline?.keyWeakness;

  // Gap-derived insights point at their exact gap row (its real source).
  if (high[0]) {
    const g = high[0];
    out.push({
      id: 'gap-high-1',
      type: 'High',
      headline: `${g.attribute} confusion is hurting ${g.modelsAffected}/${tm} rankings`,
      desc: `${g.gapReason} • Affecting ${g.modelsAffected} models`,
      target: { kind: 'gap', attribute: g.attribute },
    });
  }
  // Domain insight comes from the Evidence & Signals domain-attribution table.
  if (topDomain) {
    out.push({
      id: 'domain-strategic',
      type: 'Strategic',
      headline: `${String(topDomain.domain || '').replace('.com', '')} shaped ${topDomain.models}/${tm} models, getting featured is critical`,
      desc: `This source carried ${topDomain.influence}/10 influence across ${topDomain.models} of ${tm} models. Missing here means missing the evidence layer.`,
      target: { kind: 'stage', stageId: 'evidence-signals' },
    });
  }
  // Weakness insight comes from the competitive comparison (understanding-decisions).
  if (weakness) {
    out.push({
      id: 'weakness-medium',
      type: 'Medium',
      headline: `${weakness} could drop you from the top 3`,
      desc: 'Multiple models flagged this as a weakness. Competitors could exploit the gap to overtake your position.',
      target: { kind: 'stage', stageId: 'understanding-decisions' },
    });
  }
  if (secondHigh) {
    const g = secondHigh;
    out.push({
      id: 'gap-high-2',
      type: 'High',
      headline: `${g.attribute} data quality issue across ${g.modelsAffected} models`,
      desc: `${g.gapReason} • Impacting ${String(g.impact || '').toLowerCase()}`,
      target: { kind: 'gap', attribute: g.attribute },
    });
  }

  return out.slice(0, 4).map((o, i) => ({
    id: o.id,
    idx: String(i + 1).padStart(2, '0'),
    badge: OPT_BADGE_LABEL[o.type],
    type: o.type,
    headline: o.headline,
    desc: o.desc,
    target: o.target,
    chipLabel: o.target.kind === 'stage' ? `Stage ${STAGE_NUMBER[o.target.stageId]}` : 'Gaps',
  }));
}

// ── variance callouts (stage 2): ported summarize logic ─────────────────────
function pickValueFromMap(
  values: Record<string, unknown>,
  modelId: string,
  modelLabel: (id: string) => string
): string {
  const direct = [modelId, modelId.toLowerCase(), modelId.toUpperCase(), modelLabel(modelId), modelLabel(modelId).toLowerCase()];
  for (const k of direct) {
    const v = values[k];
    if (v != null && String(v).trim() !== '') return String(v);
  }
  const target = normKey(modelId);
  const targetLabel = normKey(modelLabel(modelId));
  for (const k of Object.keys(values || {})) {
    const nk = normKey(k);
    if (nk.includes(target) || nk.includes(targetLabel)) {
      const v = values[k];
      if (v != null && String(v).trim() !== '') return String(v);
    }
  }
  return '';
}

function summarizeVariance(values: string[]): string {
  const uniq = Array.from(new Set(values.map((v) => v.trim()).filter(Boolean)));
  if (uniq.length === 0) return '—';
  if (uniq.length === 1) return uniq[0];
  const parsed = uniq.map((v) => {
    const m = v.match(/^\s*([\d.]+)\s*(.*)\s*$/);
    if (!m) return null;
    const num = Number(m[1]);
    if (!Number.isFinite(num)) return null;
    return { num, unit: (m[2] || '').trim(), raw: v };
  });
  if (parsed.every(Boolean)) {
    const list = parsed as Array<{ num: number; unit: string; raw: string }>;
    const unit = list[0].unit;
    if (list.every((x) => x.unit === unit)) {
      const min = Math.min(...list.map((x) => x.num));
      const max = Math.max(...list.map((x) => x.num));
      return `${min} → ${max}${unit ? ` ${unit}` : ''}`;
    }
  }
  return `${uniq[0]} → ${uniq[uniq.length - 1]}`;
}

export function buildStageVariances(
  runResult: CognitionRunData | null,
  selectedModelIds: string[],
  modelLabel: (id: string) => string
): StageVarianceVM[] {
  const attributeVarianceData = runResult?.attributeVarianceData;
  const attributeData = runResult?.attributeData || [];
  const items: Array<{ attribute: string; summary: string; uniq: number }> = [];

  if (Array.isArray(attributeVarianceData) && attributeVarianceData.length) {
    for (const it of attributeVarianceData) {
      const vals = Object.values(it?.values || {}).map((v) => String(v || '')).filter(Boolean);
      const uniq = new Set(vals.map((v) => v.trim()).filter(Boolean)).size;
      if (uniq >= 2) items.push({ attribute: it.attribute, summary: summarizeVariance(vals), uniq });
    }
  } else {
    for (const row of attributeData) {
      const values = row?.values && typeof row.values === 'object' ? row.values : {};
      const collected: string[] = [];
      for (const mid of selectedModelIds || []) {
        const v = pickValueFromMap(values as Record<string, unknown>, mid, modelLabel);
        if (v && v !== '—') collected.push(v);
      }
      const uniq = new Set(collected.map((v) => v.trim()).filter(Boolean)).size;
      if (uniq >= 2) items.push({ attribute: row.attribute, summary: summarizeVariance(collected), uniq });
    }
  }

  return items
    .sort((a, b) => b.uniq - a.uniq)
    .slice(0, 3)
    .map((it, i) => ({
      label: it.attribute,
      detail: it.summary,
      color: i === 0 ? 'var(--infrared-coral)' : 'var(--warning-text)',
    }));
}

// ── pipeline stages (real run) ───────────────────────────────────────────────
function stageMeta(stage: CognitiveStage): { headline: string; meta: string[] } {
  const headline =
    stage.blurb?.headline ||
    stage.narrativeHook ||
    stage.keyTakeaway ||
    stage.blurb?.metricsLine ||
    String(stage.title || '');
  const meta = (Array.isArray(stage.metrics) ? stage.metrics : [])
    .slice(0, 3)
    .map((m) => [m.value, m.label].filter(Boolean).join(' ').trim())
    .filter(Boolean);
  return { headline, meta };
}

export function buildStages(
  runResult: CognitionRunData | null,
  selectedModelIds: string[],
  modelLabel: (id: string) => string
): StageVM[] {
  const stages = Array.isArray(runResult?.cognitiveStages) ? runResult!.cognitiveStages : [];
  const taskSetupData = runResult?.taskSetupData || [];
  const gapsData = runResult?.gapsData || [];
  const attributeData = runResult?.attributeData || [];
  const baseline = runResult?.baselineSummaryData || null;

  // Order by the canonical pipeline order, falling back to backend order.
  const byId = new Map<string, CognitiveStage>();
  for (const s of stages) byId.set(String(s.id), s);
  const ordered = STAGE_ORDER.map((id) => byId.get(id)).filter(Boolean) as CognitiveStage[];
  const list = ordered.length ? ordered : stages;

  const variances = buildStageVariances(runResult, selectedModelIds, modelLabel);

  return list.map((stage) => {
    const id = stage.id as StageId;
    const { priority, reason } = computeStagePriority({ stageId: id, taskSetupData, gapsData, attributeData, baseline });
    const { headline, meta } = stageMeta(stage);
    const whyMatters =
      (Array.isArray(stage.whyThisMatters) && stage.whyThisMatters.length
        ? stage.whyThisMatters.join(' ')
        : stage.blurb?.body) || STAGE_WHY_FALLBACK[id] || '';

    return {
      id,
      num: STAGE_NUMBER[id] || String(stage.stageNumber || ''),
      title: String(stage.title || ''),
      summary: String(stage.blurb?.metricsLine || stage.modelBehaviorInsight || ''),
      accent: STAGE_ACCENTS[id] || stage.accentColor || 'var(--signal-blue)',
      priority,
      priorityReason: reason,
      whyMatters,
      lede: { headline, meta },
      takeaway: String(stage.keyTakeaway || ''),
      variances: id === 'evidence-signals' ? variances : undefined,
      asks: STAGE_ASKS[id] || [],
    };
  });
}

// Preview stages (pre-run): labels only, non-expandable.
export function buildPreviewStages(): StageVM[] {
  return PREVIEW_STAGES.map((s) => ({
    id: s.id,
    num: s.num,
    title: s.title,
    summary: s.summary,
    accent: s.accent,
    asks: [],
  }));
}

// ── gaps ─────────────────────────────────────────────────────────────────────
function gapSeverityLabel(raw: string): GapSeverity {
  const v = String(raw || '').toLowerCase();
  if (v === 'high') return 'High';
  if (v === 'low') return 'Low';
  return 'Moderate';
}

// Mirrors the old gaps-section fallback when the backend sends no impact text.
function inferImpact(attribute: string, sev: GapSeverity): string {
  const a = (attribute || 'this attribute').trim() || 'this attribute';
  if (sev === 'High') return `High risk of inconsistent recommendations around ${a}.`;
  if (sev === 'Moderate') return `Moderate risk of inconsistent answers around ${a}.`;
  return `Minor inconsistency around ${a}.`;
}

export function buildGaps(gapsData: Gap[] | undefined): { rows: GapRowVM[]; summary: GapsSummaryVM } {
  const list = Array.isArray(gapsData) ? gapsData : [];
  const rows: GapRowVM[] = list.map((g) => {
    const severity = gapSeverityLabel(String(g.severity));
    const impact = g.impact && String(g.impact).trim() ? String(g.impact) : inferImpact(g.attribute, severity);
    return {
      attribute: g.attribute,
      reason: g.gapReason,
      models: String(g.modelsAffected ?? '—'),
      severity,
      impact,
    };
  });
  const summary: GapsSummaryVM = {
    total: rows.length,
    high: rows.filter((r) => r.severity === 'High').length,
    moderate: rows.filter((r) => r.severity === 'Moderate').length,
    low: rows.filter((r) => r.severity === 'Low').length,
  };
  return { rows, summary };
}

// ── history ──────────────────────────────────────────────────────────────────
function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso || '—');
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Prefer the /10 score the backend sends; fall back to deriving it from the
// /100 form. Returns a one-decimal string ("8.4") or '—' when no score exists.
function historyScore(h: CognitionHistoryItem): string {
  const s10 =
    h.overallScore10 != null
      ? Number(h.overallScore10)
      : h.overallScore != null
        ? Number(h.overallScore) / 10
        : null;
  if (s10 == null || !Number.isFinite(s10)) return '—';
  return s10.toFixed(1);
}

export function buildHistory(items: CognitionHistoryItem[] | undefined): HistoryRowVM[] {
  return (items || []).map((h) => {
    const runId = Number(h.runId ?? h.id);
    return {
      runId,
      run: String(runId),
      product: h.productName || '—',
      models: `${h.modelCount ?? 0} models`,
      when: formatWhen(h.timestamp),
      score: historyScore(h),
    };
  });
}
